export const queryKeys = {
  auth: {
    all: ['auth'],
    // Rule 68: sessions list is server-paginated/sorted, so the key carries
    // params. `sessionsAll` is the 2-element prefix for invalidating every
    // page/sort variant at once (TanStack partial-match).
    sessions: (params = {}) => ['auth', 'sessions', params],
    sessionsAll: ['auth', 'sessions'],
  },
  notifications: {
    all: ['notifications'],
    list: (params = {}) => ['notifications', 'list', params],
  },
  // Authorized Devices (Rule 73)
  devices: {
    all: ['devices'],
    list: () => ['devices', 'list'],
  },
  oauthClients: {
    all: ['oauth-clients'],
    list: (params = {}) => ['oauth-clients', 'list', params],
  },
  // ── MedTransact business modules ───────────────────────────────────────
  providers: {
    all: ['providers'],
    list: (params = {}) => ['providers', 'list', params],
    detail: (id) => ['providers', 'detail', id],
    affiliations: (providerId) => ['providers', 'affiliations', providerId],
  },
  diagnoses: {
    all: ['diagnoses'],
    list: (params = {}) => ['diagnoses', 'list', params],
  },
  cpts: {
    all: ['cpts'],
    list: (params = {}) => ['cpts', 'list', params],
    rates: (cptId) => ['cpts', 'rates', cptId],
  },
  physicians: {
    all: ['physicians'],
    list: (params = {}) => ['physicians', 'list', params],
    affiliations: (physicianId) => ['physicians', 'affiliations', physicianId],
  },
  corporates: {
    all: ['corporates'],
    list: (params = {}) => ['corporates', 'list', params],
  },
  products: {
    all: ['products'],
    list: (params = {}) => ['products', 'list', params],
  },
  planSetups: {
    all: ['plan-setups'],
    list: (params = {}) => ['plan-setups', 'list', params],
  },
  members: {
    all: ['members'],
    list: (params = {}) => ['members', 'list', params],
    transactions: (memberId, params = {}) => ['members', memberId, 'transactions', params],
    transactionsAll: (memberId) => ['members', memberId, 'transactions'],
  },
  payments: {
    all: ['payments'],
    list: (params = {}) => ['payments', 'list', params],
  },
  misReports: {
    all: ['mis-reports'],
    summary: () => ['mis-reports', 'summary'],
    transactions: (params = {}) => ['mis-reports', 'transactions', params],
  },
  oauthUsers: {
    all: ['oauth-users'],
    list: (params = {}) => ['oauth-users', 'list', params],
    detail: (userId) => ['oauth-users', 'detail', userId],
    scopes: () => ['oauth-users', 'scopes-catalog'],
  },
  systemSettings: {
    all: ['system-settings'],
    list: () => ['system-settings', 'list'],
  },
  exampleBatchUploads: {
    all: ['example-batch-uploads'],
    list: (params = {}) => ['example-batch-uploads', 'list', params],
  },
  uploadShareTokens: {
    all: ['upload-share-tokens'],
    list: (params = {}) => ['upload-share-tokens', 'list', params],
    sourceFile: (id) => ['upload-share-tokens', 'source-file', id],
  },
  chat: {
    all: ['chat'],
    threadsAll: ['chat', 'threads'],
    threads: (params = {}) => ['chat', 'threads', params],
    messages: (threadId, params = {}) => ['chat', 'messages', threadId, params],
    participants: (threadId) => ['chat', 'participants', threadId],
    settings: () => ['chat', 'settings'],
    // Rule 74 v1.68 — per-user mute list (suppresses ringtone for muted callers).
    mutes: () => ['chat', 'mutes'],
    // Phase B — per-thread mute list (suppresses CHAT_NOTIFICATION for muted threads).
    threadMutes: () => ['chat', 'thread-mutes'],
    // Phase E — user block list (bidirectional cut for chat + 1:1 calls).
    blocks: () => ['chat', 'blocks'],
    // Phase B — single-thread View side-panel summary (peer/media/files/links/mute).
    threadView: (threadId) => ['chat', 'thread-view', threadId],
    // Phase C — pinned messages per thread.
    pinned: (threadId) => ['chat', 'pinned', threadId],
  },

  // Voice + Video Calls (Architecture Rule 74)
  calls: {
    all: ['calls'],
    detail: (callId) => ['calls', 'detail', callId],
    thread: (threadId, params = {}) => ['calls', 'thread', threadId, params],
    recordingUrl: (callId) => ['calls', 'recording-url', callId],
  },

  // Bloom Stranger (Architecture Rule 75)
  bloom: {
    all: ['bloom'],
    current: () => ['bloom', 'current'],
    history: (params = {}) => ['bloom', 'history', params],
    adminReports: (params = {}) => ['bloom', 'admin-reports', params],
  },

  // Bloom Gifts (Architecture Rule 76)
  bloomGifts: {
    all: ['bloom-gifts'],
    single: (publicId) => ['bloom-gifts', 'single', publicId],
    thread: (threadId, params = {}) => ['bloom-gifts', 'thread', threadId, params],
    wallet: () => ['bloom-gifts', 'wallet', 'me'],
    walletPrefix: ['bloom-gifts', 'wallet'],
    walletLedger: (params = {}) => ['bloom-gifts', 'wallet', 'me', 'ledger', params],
    receivedHistory: (params = {}) => ['bloom-gifts', 'received', 'me', params],
    myWithdrawals: (params = {}) => ['bloom-gifts', 'withdrawals', 'me', params],
    adminList: (params = {}) => ['bloom-gifts', 'admin-list', params],
    adminWithdrawals: (params = {}) => ['bloom-gifts', 'admin-withdrawals', params],
  },

  // System-Administrator read-only viewers (Rule 68)
  auditLogs: {
    all: ['admin-audit-logs'],
    list: (params = {}) => ['admin-audit-logs', 'list', params],
    export: (id) => ['admin-audit-logs', 'export', id],
  },
  outboundHttpLogs: {
    all: ['admin-outbound-http-logs'],
    list: (params = {}) => ['admin-outbound-http-logs', 'list', params],
  },
  fieldHistory: {
    all: ['admin-field-history'],
    list: (params = {}) => ['admin-field-history', 'list', params],
  },
  loginAttempts: {
    all: ['admin-login-attempts'],
    list: (params = {}) => ['admin-login-attempts', 'list', params],
  },
  chatMessagesAdmin: {
    all: ['admin-chat-messages'],
    list: (params = {}) => ['admin-chat-messages', 'list', params],
    thread: (threadId, params = {}) => ['admin-chat-messages', 'thread', threadId, params],
  },
  webhookDeliveries: {
    all: ['admin-webhook-deliveries'],
    list: (params = {}) => ['admin-webhook-deliveries', 'list', params],
  },
  failedJobs: {
    all: ['admin-failed-jobs'],
    list: (params = {}) => ['admin-failed-jobs', 'list', params],
  },
};
